import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Camera, X, Loader2, MessageSquareReply } from 'lucide-react';
import StarRating from './StarRating';
import { resolveImageUrl } from '../utils/imageUrl';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return '';
  }
};

const ReviewsSection = ({ productId, productName, onSummaryChange, title, subtitle, scrollable = false }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);

  const [form, setForm] = useState({
    customer_name: '',
    rating: 5,
    title: '',
    message: '',
    images: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products/${productId}/reviews`);
      setReviews(res.data);
      if (onSummaryChange) {
        const count = res.data.length;
        const avg = count ? res.data.reduce((a, r) => a + (r.rating || 0), 0) / count : 0;
        onSummaryChange({ average: avg, count });
      }
    } catch (err) {
      console.error('Failed to fetch reviews', err);
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => {
    const count = reviews.length;
    const avg = count ? reviews.reduce((a, r) => a + (r.rating || 0), 0) / count : 0;
    const histogram = [5, 4, 3, 2, 1].map((star) => ({
      star,
      count: reviews.filter((r) => r.rating === star).length,
    }));
    return { count, avg, histogram };
  }, [reviews]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (form.images.length + files.length > 5) {
      toast({ title: 'Maximum 5 images allowed', variant: 'destructive' });
      return;
    }
    setUploadingImg(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) {
          toast({ title: `${f.name} is over 5MB`, variant: 'destructive' });
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${API}/reviews/upload-image`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(res.data.url);
      }
      setForm((prev) => ({ ...prev, images: [...prev.images, ...uploaded] }));
    } catch (err) {
      console.error(err);
      toast({ title: 'Image upload failed', variant: 'destructive' });
    } finally {
      setUploadingImg(false);
      e.target.value = '';
    }
  };

  const removeImage = (url) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((u) => u !== url) }));
  };

  const resetForm = () => {
    setForm({ customer_name: '', rating: 5, title: '', message: '', images: [] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) {
      toast({ title: 'Please enter your name', variant: 'destructive' });
      return;
    }
    if (!form.message.trim()) {
      toast({ title: 'Please enter your review', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId,
        ...form,
      });
      toast({ title: 'Review submitted!', description: 'Thanks for sharing your feedback.' });
      resetForm();
      setShowForm(false);
      fetchReviews();
    } catch (err) {
      console.error(err);
      toast({
        title: 'Failed to submit',
        description: err.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-8 sm:mt-12 bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8" data-testid="reviews-section">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{title || 'Ratings & Reviews'}</h2>
          <p className="text-sm text-gray-600 mt-1">{subtitle || `See what customers say about ${productName}`}</p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2.5 px-5 rounded-lg transition-colors text-sm sm:text-base"
          data-testid="write-review-btn"
        >
          {showForm ? 'Cancel' : 'Write a Review'}
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8 border-b pb-6">
        <div className="text-center md:text-left" data-testid="reviews-summary">
          <div className="text-4xl sm:text-5xl font-bold text-gray-900">
            {summary.count ? summary.avg.toFixed(1) : '0.0'}
          </div>
          <StarRating value={summary.avg} size={22} testIdPrefix="summary-star" />
          <p className="text-sm text-gray-600 mt-1">
            {summary.count} {summary.count === 1 ? 'review' : 'reviews'}
          </p>
        </div>
        <div className="md:col-span-2 space-y-1.5">
          {summary.histogram.map(({ star, count }) => {
            const pct = summary.count ? (count / summary.count) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                <span className="w-8 text-gray-700">{star}★</span>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-yellow-500" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-8 text-right text-gray-600">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Review form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="border rounded-lg p-4 sm:p-6 mb-6 bg-gray-50" data-testid="review-form">
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Your Name*</label>
            <input
              type="text"
              required
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              data-testid="review-name-input"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Your Rating*</label>
            <StarRating
              value={form.rating}
              onChange={(v) => setForm({ ...form, rating: v })}
              size={28}
              testIdPrefix="rating-input"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Review Title <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              type="text"
              maxLength={120}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Sum up your experience in one line"
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              data-testid="review-title-input"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Your Review*</label>
            <textarea
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Share the details of your experience"
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-y"
              data-testid="review-message-input"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Photos (optional, up to 5)</label>
            <div className="flex flex-wrap gap-2 items-center">
              {form.images.map((url) => (
                <div key={url} className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border">
                  <img src={resolveImageUrl(url)} alt="review" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(url)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                    aria-label="Remove image"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {form.images.length < 5 && (
                <label className="w-16 h-16 sm:w-20 sm:h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-yellow-500 transition-colors text-gray-500">
                  {uploadingImg ? <Loader2 className="animate-spin" size={20} /> : <Camera size={20} />}
                  <span className="text-[10px] mt-1">Add</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={uploadingImg}
                    data-testid="review-image-input"
                  />
                </label>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="bg-black hover:bg-gray-800 text-white font-bold py-2.5 px-6 rounded-lg disabled:opacity-60 flex items-center gap-2"
              data-testid="submit-review-btn"
            >
              {submitting && <Loader2 className="animate-spin" size={16} />}
              Submit Review
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); resetForm(); }}
              className="border border-gray-300 hover:bg-gray-100 py-2.5 px-6 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Reviews list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="no-reviews-msg">
          No reviews yet. Be the first to review this product!
        </div>
      ) : (
        <div
          className={`space-y-4 sm:space-y-6 ${scrollable ? 'max-h-[300px] overflow-y-auto pr-2 sm:pr-3 border rounded-lg p-3 sm:p-4 bg-gray-50/50' : ''}`}
          data-testid="reviews-list"
        >
          {reviews.map((r) => (
            <div key={r.id} className="border rounded-lg p-4 sm:p-5" data-testid={`review-item-${r.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StarRating value={r.rating} size={16} testIdPrefix={`rev-${r.id}-star`} />
                    {r.title && <h3 className="font-bold text-gray-900 text-sm sm:text-base">{r.title}</h3>}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    By <span className="font-semibold text-gray-700">{r.customer_name}</span> · {formatDate(r.created_at)}
                  </p>
                </div>
              </div>
              <p className="text-gray-700 text-sm sm:text-base mt-3 whitespace-pre-line">{r.message}</p>
              {r.images && r.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {r.images.map((url) => (
                    <button
                      type="button"
                      key={url}
                      onClick={() => setPreviewImg(resolveImageUrl(url))}
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border hover:opacity-90"
                    >
                      <img src={resolveImageUrl(url)} alt="Customer" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              {r.admin_reply && r.admin_reply.trim() && (
                <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded" data-testid={`admin-reply-${r.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquareReply size={16} className="text-yellow-700" />
                    <span className="text-sm font-bold text-yellow-800">Reply from Jain Watch Company</span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{r.admin_reply}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Image lightbox */}
      {previewImg && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImg(null)}
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black"
            onClick={() => setPreviewImg(null)}
            aria-label="Close preview"
          >
            <X />
          </button>
          <img src={previewImg} alt="Preview" className="max-h-[85vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;
